import React, { useMemo, useState } from 'react';
import { Download, Settings, Trash2, Upload, Save } from 'lucide-react';
import FloorPlanCanvas from './FloorPlanCanvas';
import type { FloorPlan, TablePosition } from '../types';

interface FloorPlanEditorProps {
  floorPlan: FloorPlan;
  onFloorPlanUpdate: (floorPlan: FloorPlan) => void;
  onSave: (floorPlan: FloorPlan) => void;
}

type TableShape = TablePosition['shape'];

const TABLE_PRESETS: Array<{
  shape: TableShape;
  label: string;
  description: string;
  width: number;
  height: number;
  capacity: number;
  color: string;
}> = [
  {
    shape: 'round',
    label: 'Round Dining',
    description: 'Best for guest tables and banquet seating.',
    width: 88,
    height: 88,
    capacity: 8,
    color: '#efe4d3',
  },
  {
    shape: 'square',
    label: 'Square Table',
    description: 'Good for smaller lounges or compact seating.',
    width: 84,
    height: 84,
    capacity: 4,
    color: '#f3e9db',
  },
  {
    shape: 'rectangle',
    label: 'Banquet Table',
    description: 'Ideal for head tables, family tables, or VIP rows.',
    width: 144,
    height: 68,
    capacity: 10,
    color: '#ead8c4',
  },
];

function TablePreviewIcon({
  shape,
  size = 36,
}: {
  shape: TableShape;
  size?: number;
}) {
  const commonProps = {
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  if (shape === 'round') {
    return (
      <svg width={size} height={size} viewBox="0 0 36 36" aria-hidden="true">
        <circle cx="18" cy="18" r="9" fill="currentColor" opacity="0.18" {...commonProps} />
        <circle cx="18" cy="5.5" r="2.7" fill="white" {...commonProps} />
        <circle cx="28.2" cy="10.2" r="2.7" fill="white" {...commonProps} />
        <circle cx="30" cy="21" r="2.7" fill="white" {...commonProps} />
        <circle cx="23.5" cy="30.2" r="2.7" fill="white" {...commonProps} />
        <circle cx="12.5" cy="30.2" r="2.7" fill="white" {...commonProps} />
        <circle cx="6" cy="21" r="2.7" fill="white" {...commonProps} />
        <circle cx="7.8" cy="10.2" r="2.7" fill="white" {...commonProps} />
      </svg>
    );
  }

  if (shape === 'square') {
    return (
      <svg width={size} height={size} viewBox="0 0 36 36" aria-hidden="true">
        <rect x="9" y="9" width="18" height="18" rx="3" fill="currentColor" opacity="0.18" {...commonProps} />
        <rect x="13" y="3.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
        <rect x="18.5" y="3.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
        <rect x="13" y="27.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
        <rect x="18.5" y="27.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
        <rect x="3.5" y="13" width="5" height="4.5" rx="1.4" fill="white" {...commonProps} />
        <rect x="3.5" y="18.5" width="5" height="4.5" rx="1.4" fill="white" {...commonProps} />
        <rect x="27.5" y="13" width="5" height="4.5" rx="1.4" fill="white" {...commonProps} />
        <rect x="27.5" y="18.5" width="5" height="4.5" rx="1.4" fill="white" {...commonProps} />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 36 36" aria-hidden="true">
      <rect x="6.5" y="11" width="23" height="14" rx="4" fill="currentColor" opacity="0.18" {...commonProps} />
      <rect x="10" y="4" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="15.75" y="4" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="21.5" y="4" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="10" y="27" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="15.75" y="27" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="21.5" y="27" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="2.8" y="15.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
      <rect x="28.7" y="15.5" width="4.5" height="5" rx="1.4" fill="white" {...commonProps} />
    </svg>
  );
}

const getPreset = (shape: TableShape) =>
  TABLE_PRESETS.find((preset) => preset.shape === shape) || TABLE_PRESETS[0];

export default function FloorPlanEditor({ floorPlan, onFloorPlanUpdate, onSave }: FloorPlanEditorProps) {
  const [selectedTable, setSelectedTable] = useState<TablePosition | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const sortedTables = useMemo(
    () => [...floorPlan.tables].sort((a, b) => a.tableNumber - b.tableNumber),
    [floorPlan.tables],
  );

  const getNextTableNumber = () => {
    const existingNumbers = floorPlan.tables.map((table) => table.tableNumber);
    let nextNumber = 1;

    while (existingNumbers.includes(nextNumber)) {
      nextNumber++;
    }

    return nextNumber;
  };

  const addTable = (shape: TableShape) => {
    const preset = getPreset(shape);
    const index = floorPlan.tables.length;
    const suggestedX = 80 + (index % 4) * 150;
    const suggestedY = 80 + Math.floor(index / 4) * 130;

    const newTable: TablePosition = {
      id: crypto.randomUUID(),
      x: Math.max(20, Math.min(floorPlan.width - preset.width - 20, suggestedX)),
      y: Math.max(20, Math.min(floorPlan.height - preset.height - 20, suggestedY)),
      width: preset.width,
      height: preset.height,
      rotation: 0,
      shape,
      tableNumber: getNextTableNumber(),
      capacity: preset.capacity,
      color: preset.color,
    };

    onFloorPlanUpdate({
      ...floorPlan,
      tables: [...floorPlan.tables, newTable],
      updatedAt: new Date().toISOString(),
    });

    setSelectedTable(newTable);
  };

  const updateTable = (updatedTable: TablePosition) => {
    const updatedTables = floorPlan.tables.map((table) =>
      table.id === updatedTable.id ? updatedTable : table,
    );

    onFloorPlanUpdate({
      ...floorPlan,
      tables: updatedTables,
      updatedAt: new Date().toISOString(),
    });

    setSelectedTable(updatedTable);
  };

  const deleteTable = (tableId: string) => {
    if (!confirm('Are you sure you want to delete this table?')) {
      return;
    }

    onFloorPlanUpdate({
      ...floorPlan,
      tables: floorPlan.tables.filter((table) => table.id !== tableId),
      updatedAt: new Date().toISOString(),
    });

    setSelectedTable(null);
  };

  const updateFloorPlanSettings = (updates: Partial<FloorPlan>) => {
    onFloorPlanUpdate({
      ...floorPlan,
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  };

  const exportFloorPlan = () => {
    const dataStr = JSON.stringify(floorPlan, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${floorPlan.name || 'floor-plan'}.json`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const importFloorPlan = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const importedPlan = JSON.parse(loadEvent.target?.result as string);
        onFloorPlanUpdate({
          ...importedPlan,
          id: floorPlan.id,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        alert('Error importing floor plan. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const applyShapePreset = (shape: TableShape) => {
    if (!selectedTable) {
      return;
    }

    const preset = getPreset(shape);

    updateTable({
      ...selectedTable,
      shape,
      width: preset.width,
      height: preset.height,
      capacity: preset.capacity,
      color: selectedTable.color || preset.color,
    });
  };

  return (
    <div className="floor-plan-editor">
      <div className="floor-plan-toolbar">
        <div className="floor-plan-toolbar__section">
          <span className="floor-plan-toolbar__label">Add Seating</span>
          <div className="floor-plan-preset-grid">
            {TABLE_PRESETS.map((preset) => (
              <button
                key={preset.shape}
                type="button"
                className="table-preset-button"
                onClick={() => addTable(preset.shape)}
                title={preset.label}
              >
                <div className="table-preset-button__icon">
                  <TablePreviewIcon shape={preset.shape} />
                </div>
                <div className="table-preset-button__copy">
                  <strong>{preset.label}</strong>
                  <span>{preset.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="floor-plan-toolbar__section">
          <span className="floor-plan-toolbar__label">Actions</span>
          <div className="floor-plan-toolbar__actions">
            <button
              type="button"
              onClick={() => setShowSettings((value) => !value)}
              className="btn btn-small btn-secondary"
              title="Floor Plan Settings"
            >
              <Settings size={16} />
              {showSettings ? 'Hide Settings' : 'Venue Settings'}
            </button>

            <button
              type="button"
              onClick={() => onSave(floorPlan)}
              className="btn btn-small"
              title="Save Floor Plan"
            >
              <Save size={16} />
              Save Layout
            </button>

            <button
              type="button"
              onClick={exportFloorPlan}
              className="btn btn-small btn-secondary"
              title="Export Floor Plan"
            >
              <Download size={16} />
              Export
            </button>

            <label className="btn btn-small btn-secondary floor-plan-file-button" title="Import Floor Plan">
              <Upload size={16} />
              Import
              <input
                type="file"
                accept=".json"
                onChange={importFloorPlan}
                style={{ display: 'none' }}
              />
            </label>

            {selectedTable && (
              <button
                type="button"
                onClick={() => deleteTable(selectedTable.id)}
                className="btn btn-small btn-danger"
                title="Delete Selected Table"
              >
                <Trash2 size={16} />
                Delete Table
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="floor-plan-editor__workspace">
        <div className="floor-plan-editor__canvas">
          <div className="floor-plan-editor__canvas-header">
            <div>
              <h3>{floorPlan.name || 'Venue Layout'}</h3>
              <p>Drag tables to reposition them. Use zoom controls to refine the layout.</p>
            </div>
            <div className="mono-chip">
              {floorPlan.tables.length} table{floorPlan.tables.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="floor-plan-editor__canvas-stage">
            <FloorPlanCanvas
              floorPlan={floorPlan}
              onTableUpdate={updateTable}
              onTableSelect={setSelectedTable}
              selectedTable={selectedTable}
              isEditable={true}
              className="floor-plan-canvas"
            />
          </div>
        </div>

        <aside className="floor-plan-editor__sidebar">
          {showSettings && (
            <div className="card floor-plan-sidebar-section">
              <h3>Venue Settings</h3>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="input"
                  value={floorPlan.name}
                  onChange={(e) => updateFloorPlanSettings({ name: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Width (px)</label>
                  <input
                    type="number"
                    className="input"
                    value={floorPlan.width}
                    onChange={(e) => updateFloorPlanSettings({ width: parseInt(e.target.value, 10) || 800 })}
                  />
                </div>
                <div className="form-group">
                  <label>Height (px)</label>
                  <input
                    type="number"
                    className="input"
                    value={floorPlan.height}
                    onChange={(e) => updateFloorPlanSettings({ height: parseInt(e.target.value, 10) || 600 })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Floor Tone</label>
                <input
                  type="color"
                  className="input"
                  value={floorPlan.background || '#f7efe6'}
                  onChange={(e) => updateFloorPlanSettings({ background: e.target.value })}
                />
              </div>
            </div>
          )}

          {selectedTable ? (
            <div className="card floor-plan-sidebar-section">
              <div className="floor-plan-selected-header">
                <div className="floor-plan-selected-header__icon">
                  <TablePreviewIcon shape={selectedTable.shape} size={42} />
                </div>
                <div>
                  <h3>Table {selectedTable.tableNumber}</h3>
                  <p>
                    {selectedTable.capacity} seats
                    {' • '}
                    {getPreset(selectedTable.shape).label}
                  </p>
                </div>
              </div>

              <div className="form-group">
                <label>Table Type</label>
                <div className="floor-plan-shape-options">
                  {TABLE_PRESETS.map((preset) => (
                    <button
                      key={preset.shape}
                      type="button"
                      className={`floor-plan-shape-option ${selectedTable.shape === preset.shape ? 'floor-plan-shape-option--active' : ''}`}
                      onClick={() => applyShapePreset(preset.shape)}
                    >
                      <TablePreviewIcon shape={preset.shape} size={28} />
                      <span>{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Table Number</label>
                  <input
                    type="number"
                    className="input"
                    value={selectedTable.tableNumber}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      tableNumber: parseInt(e.target.value, 10) || 1,
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Capacity</label>
                  <input
                    type="number"
                    className="input"
                    value={selectedTable.capacity}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      capacity: parseInt(e.target.value, 10) || 1,
                    })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Width</label>
                  <input
                    type="number"
                    className="input"
                    value={selectedTable.width}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      width: parseInt(e.target.value, 10) || 50,
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Height</label>
                  <input
                    type="number"
                    className="input"
                    value={selectedTable.height}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      height: parseInt(e.target.value, 10) || 50,
                    })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rotation</label>
                  <input
                    type="number"
                    className="input"
                    value={selectedTable.rotation}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      rotation: parseInt(e.target.value, 10) || 0,
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Table Color</label>
                  <input
                    type="color"
                    className="input"
                    value={selectedTable.color || getPreset(selectedTable.shape).color}
                    onChange={(e) => updateTable({
                      ...selectedTable,
                      color: e.target.value,
                    })}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="card floor-plan-sidebar-section floor-plan-sidebar-section--empty">
              <div className="floor-plan-empty-state-icon">
                <TablePreviewIcon shape="round" size={48} />
              </div>
              <h3>Select a table</h3>
              <p>Pick any table on the layout to change its type, size, seat count, and position.</p>
            </div>
          )}

          <div className="card floor-plan-sidebar-section">
            <h3>Layout Guide</h3>
            <p className="floor-plan-help">
              Use round tables for main guest seating, square tables for compact lounge zones, and banquet tables for long family or VIP rows.
            </p>
          </div>

          <div className="card floor-plan-sidebar-section">
            <h3>Tables ({floorPlan.tables.length})</h3>
            <div className="floor-plan-table-list">
              {sortedTables.map((table) => (
                <button
                  key={table.id}
                  type="button"
                  className={`floor-plan-table-item ${selectedTable?.id === table.id ? 'floor-plan-table-item--selected' : ''}`}
                  onClick={() => setSelectedTable(table)}
                >
                  <div className="floor-plan-table-item__icon">
                    <TablePreviewIcon shape={table.shape} size={28} />
                  </div>
                  <div className="floor-plan-table-item__copy">
                    <strong>Table {table.tableNumber}</strong>
                    <span>{table.capacity} seats</span>
                  </div>
                  <div
                    className="floor-plan-table-item__swatch"
                    style={{ background: table.color || getPreset(table.shape).color }}
                  />
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
